import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { trackClickMessage, trackOfferMessageTeaserShow } from './tracking';
import { retrySendMessageById } from '../../../../actions/sendMessage/retrySendMessageById';
import { OfferMessage } from '../../../../components/OfferMessage';
import { useMessageContext } from '../../../../utils/messageContext';
import { OfferMessageLayout } from '../../../../components/OfferMessageLayout';
import { ErrorSignContainer } from '../ErrorSignContainer';
import { ErrorTextContainer } from '../ErrorTextContainer';
import { ChatMessageMetaContainer } from '../ChatMessageMetaContainer';
import { ActionButtonsContainer } from '../ActionButtonsContainer';
import { getOfferShareUrl } from './utils/getOfferShareUrl';
import { IOfferMessage } from './types';
import { ERequestApiStatus } from '../../../../types/appState';
import { useMessageText } from '../../hooks';
import { MessageReplyQuoteContainer } from '../../../MessageReplyQuoteContainer';
import { EMessageType } from '../../../../types/messageType';

export const OfferMessageContainer: React.FC = () => {
  const { messageId, messageType, messageRef, isLastInChain, message, chatId } = useMessageContext();
  const { status, offers, communicationId } = message;
  const offer = offers?.[0] as IOfferMessage | undefined;

  const dispatch = useDispatch();

  const isMessageFailed = status === ERequestApiStatus.Failed;

  const formattedText = useMessageText({ message, messageId, messageType, chatId });

  const shareUrl = offer ? getOfferShareUrl(offer, false) : '';

  const handleReload = React.useCallback(() => {
    if (offer) {
      dispatch(retrySendMessageById(chatId, messageId));
    }
  }, [offer, dispatch, chatId, messageId]);

  const handleTrackOfferMessageTeaserShow = React.useCallback(() => {
    if (offer) {
      trackOfferMessageTeaserShow({
        offerId: offer.realtyOfferId,
        block: 'Chat_snippet',
        mlSearchSessionGuid: offer.mlSearchSessionGuid ?? undefined,
        batchid: offer.batchTs ?? undefined,
        position: offer.position ?? undefined,
      });
    }
  }, [offer]);

  const onClick = React.useCallback(() => {
    if (!offer) {
      return;
    }

    trackClickMessage(offer.realtyOfferId, chatId, messageId, communicationId);
  }, [offer, chatId, messageId, communicationId]);

  if (!offer) {
    return null;
  }

  return (
    <OfferMessageLayout
      errorSign={<ErrorSignContainer />}
      errorText={<ErrorTextContainer />}
      messageType={messageType}
      messageMeta={<ChatMessageMetaContainer />}
      actionButtons={<ActionButtonsContainer />}
      replyQuote={<MessageReplyQuoteContainer />}
      ref={messageRef}
      isLastMessageInChain={isLastInChain}
      offerMessage={
        <ActionAfterViewObserver
          percentVisible={0.5}
          callback={handleTrackOfferMessageTeaserShow}
          triggerOnce={false}
          callbackDelay={1000}
        >
          <OfferMessage
            isOutcoming={messageType === EMessageType.Outcoming}
            isChatbotChat={false}
            withReload={isMessageFailed}
            {...offer}
            formattedText={formattedText}
            shareUrl={shareUrl}
            photoUrl={offer.photoUrls}
            messageRef={messageRef}
            onReload={handleReload}
            onClick={onClick}
          />
        </ActionAfterViewObserver>
      }
    />
  );
};
